import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { badgeClass, formatNumber, getApiErrorMessage, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

interface PlannedOrder {
  kind: 'make' | 'buy';
  quantity: number;
  dueDate: string;
  releaseDate: string;
  lateByDays: number;
  for: string;
}
interface PlanItem {
  productId: string;
  sku: string;
  name: string;
  unit: string;
  kind: 'make' | 'buy';
  level: number;
  bomHeaderId: string | null;
  bomNumber: string | null;
  onHand: number;
  reservedQuantity: number;
  minimumStock: number;
  leadTimeDays: number;
  leadTimeFrom: 'vendor' | 'item';
  ownLeadTimeDays: number;
  minimumOrderQuantity: number;
  preferredVendor: string | null;
  demand: number;
  scheduledReceipts: number;
  projectedBalance: number;
  suggestedQuantity: number;
  firstReleaseDate: string | null;
  late: boolean;
  plannedOrders: PlannedOrder[];
  messages: { type: string; text: string }[];
  timeline: { date: string; type: string; text: string; quantity: number; balance: number }[];
}
interface Plan {
  today: string;
  horizonDays: number;
  horizonEnd: string;
  summary: { itemsPlanned: number; itemsToBuy: number; itemsToMake: number; lateOrders: number; releaseThisWeek: number; toExpedite: number; demandLines: number; forecastLines: number };
  warnings: string[];
  data: PlanItem[];
  total: number;
  totalPages: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const HORIZONS = [30, 60, 90, 180, 365];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
const KIND: Record<PlanItem['kind'], { label: string; className: string }> = {
  make: { label: 'Make', className: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20' },
  buy: { label: 'Buy', className: 'bg-teal-50 text-teal-700 ring-teal-600/20' },
};

const mrpApi = {
  run: async (params: object): Promise<Plan> => (await api.get('/mrp', { params: clean(params) })).data,
  setLeadTime: async (productId: string, leadTimeDays: number) => (await api.put(`/mrp/items/${productId}`, { leadTimeDays })).data,
  raiseIndent: async (data: object): Promise<{ id: string; indentNumber: string }> => (await api.post('/indents', data)).data,
  createWorkOrder: async (data: object): Promise<{ id: number; workOrderNumber: string }> => (await api.post('/manufacturing/work-orders', data)).data,
};

// ---------------------------------------------------------------- one item: why, when, and what to do

const ItemPanel: React.FC<{ item: PlanItem; today: string; canMake: boolean; canEdit: boolean; onChanged: () => void; onClose: () => void }> = ({ item, today, canMake, canEdit, onChanged, onClose }) => {
  const [leadTime, setLeadTime] = useState(String(item.ownLeadTimeDays));
  const saveLeadTime = useMutation({
    mutationFn: () => mrpApi.setLeadTime(item.productId, Number(leadTime)),
    onSuccess: () => {
      toast.success(`Lead time of ${item.name} set to ${Number(leadTime)} day(s)`);
      onChanged();
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'The lead time could not be saved')),
  });
  const makeOrder = useMutation({
    mutationFn: (order: PlannedOrder) =>
      mrpApi.createWorkOrder({ productId: item.productId, bomHeaderId: item.bomHeaderId, plannedQuantity: order.quantity, plannedStartDate: order.releaseDate, plannedEndDate: order.dueDate < order.releaseDate ? order.releaseDate : order.dueDate, notes: `Suggested by MRP for ${order.for}` }),
    onSuccess: (order) => {
      toast.success(`Work order ${order.workOrderNumber} created as a draft`);
      onChanged();
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'The work order could not be created'), { duration: 7000 }),
  });
  const leadTimeValid = /^\d{1,3}$/.test(leadTime.trim()) && Number(leadTime) <= 365;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, KIND[item.kind].className)}>{KIND[item.kind].label}</span>
        <span>{item.sku}</span>
        {item.bomNumber && <span>BOM {item.bomNumber}</span>}
        {item.preferredVendor && <span>Preferred vendor {item.preferredVendor}</span>}
        <span>In stock {formatNumber(item.onHand)} {item.unit}{item.reservedQuantity > 0 ? `, ${formatNumber(item.reservedQuantity)} put aside` : ''}</span>
        {item.minimumStock > 0 && <span>Minimum {formatNumber(item.minimumStock)}</span>}
        {item.minimumOrderQuantity > 0 && <span>Ordered in lots of at least {formatNumber(item.minimumOrderQuantity)}</span>}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg bg-gray-50 p-3 ring-1 ring-gray-200">
        <label htmlFor="mrp-lead" className="text-sm text-gray-700">
          Lead time of the item, in days
          <input id="mrp-lead" type="number" min="0" max="365" step="1" value={leadTime} disabled={!canEdit} onChange={(event) => setLeadTime(event.target.value)} className={cn(inputClass, 'mt-1 w-28')} />
        </label>
        {canEdit && (
          <button type="button" onClick={() => saveLeadTime.mutate()} disabled={saveLeadTime.isPending || !leadTimeValid || Number(leadTime) === item.ownLeadTimeDays} className={secondaryButtonClass}>
            Save Lead Time
          </button>
        )}
        <p className="min-w-0 flex-1 pb-2 text-xs text-gray-500">
          {item.leadTimeFrom === 'vendor'
            ? `This plan uses ${item.leadTimeDays} day(s): the lead time of the preferred approved vendor comes before the one of the item.`
            : item.leadTimeDays === 0
              ? 'With no lead time, every suggestion is dated on the day the item is needed.'
              : `Suggestions are dated ${item.leadTimeDays} day(s) before the item is needed.`}
        </p>
      </div>

      {item.messages.length > 0 && (
        <ul className="space-y-1 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-600/20">
          {item.messages.map((message, index) => (<li key={index}>{message.text}</li>))}
        </ul>
      )}

      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Suggested {item.kind === 'make' ? 'work orders' : 'purchases'}</h4>
        {item.plannedOrders.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">Nothing to {item.kind === 'make' ? 'make' : 'buy'}: stock and what is already coming cover what is wanted.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
                  <th scope="col" className={thClass}>{item.kind === 'make' ? 'Start by' : 'Order by'}</th>
                  <th scope="col" className={thClass}>Needed on</th>
                  <th scope="col" className={thClass}>For</th>
                  {item.kind === 'make' && canMake && <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {item.plannedOrders.map((order, index) => (
                  <tr key={index}>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(order.quantity)} <span className="font-normal text-gray-500">{item.unit}</span></td>
                    <td className={cn('whitespace-nowrap px-4 py-2 text-sm', order.lateByDays > 0 ? 'font-medium text-red-600' : 'text-gray-900')}>
                      {order.releaseDate === today ? 'Today' : formatDate(order.releaseDate)}
                      {order.lateByDays > 0 && <span className="block text-xs">{order.lateByDays} day(s) late already</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{formatDate(order.dueDate)}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{order.for}</td>
                    {item.kind === 'make' && canMake && (
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        <button type="button" onClick={() => makeOrder.mutate(order)} disabled={makeOrder.isPending} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Create a work order for ${formatNumber(order.quantity)} ${item.name}`}>
                          Create Work Order
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {item.kind === 'buy' && item.plannedOrders.length > 0 && <p className="mt-2 text-xs text-gray-500">Tick the item in the list to put it on a purchase indent.</p>}
      </section>

      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Stock, day by day</h4>
        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Date</th>
                <th scope="col" className={thClass}>What</th>
                <th scope="col" className={cn(thClass, 'text-right')}>In / out</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {item.timeline.map((row, index) => (
                <tr key={index} className={cn(row.type === 'planned' && 'bg-indigo-50/50')}>
                  <td className="whitespace-nowrap px-4 py-1.5 text-sm text-gray-700">{row.date === today ? 'Today' : formatDate(row.date)}</td>
                  <td className="px-4 py-1.5 text-sm text-gray-900">{row.text}</td>
                  <td className={cn('whitespace-nowrap px-4 py-1.5 text-right text-sm tabular-nums', row.type === 'stock' ? 'text-gray-500' : row.quantity < 0 ? 'text-red-600' : 'text-green-700')}>{row.type === 'stock' ? '' : `${row.quantity > 0 ? '+' : ''}${formatNumber(row.quantity)}`}</td>
                  <td className={cn('whitespace-nowrap px-4 py-1.5 text-right text-sm font-medium tabular-nums', row.balance < 0 ? 'text-red-600' : 'text-gray-900')}>{formatNumber(row.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="flex justify-end border-t border-gray-200 pt-3">
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Mrp: React.FC = () => {
  const { can } = usePermissions();
  const canIndent = can('purchase.create');
  const canMake = can('manufacturing.create');
  const canEdit = can('manufacturing.update');
  const queryClient = useQueryClient();

  const [horizonDays, setHorizonDays] = useState(90);
  const [includeUnconfirmed, setIncludeUnconfirmed] = useState(false);
  const [keepMinimumStock, setKeepMinimumStock] = useState(true);
  const [useForecast, setUseForecast] = useState(false);
  const [show, setShow] = useState<'actions' | 'all'>('actions');
  const [kind, setKind] = useState<'make' | 'buy' | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(Math.max(25, defaultRowsPerPage()));
  const [chosen, setChosen] = useState<Record<string, PlanItem>>({});
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['mrp', { horizonDays, includeUnconfirmed, keepMinimumStock, useForecast, show, kind, search, page, limit }],
    queryFn: () => mrpApi.run({ horizonDays, includeUnconfirmed, keepMinimumStock, useForecast, show, kind, search, page, limit }),
    placeholderData: keepPreviousData,
  });
  // What was ticked is planned afresh after every run
  useEffect(() => setChosen({}), [horizonDays, includeUnconfirmed, keepMinimumStock, useForecast]);

  const rerun = () => {
    setChosen({});
    queryClient.invalidateQueries({ queryKey: ['mrp'] });
  };
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const viewing = rows.find((row) => row.productId === viewingId) ?? null;
  const picked = Object.values(chosen);
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  const raise = useMutation({
    mutationFn: () =>
      mrpApi.raiseIndent({
        // The earliest day any of it is needed
        requiredBy: picked.flatMap((item) => item.plannedOrders.map((order) => order.dueDate)).sort()[0],
        requestedFor: 'Material planning (MRP)',
        notes: `Raised from the MRP run of ${formatDate(data?.today)} for the next ${data?.horizonDays} days`,
        items: picked.map((item) => ({
          productId: item.productId,
          quantity: item.suggestedQuantity,
          notes: item.plannedOrders.map((order) => `${formatNumber(order.quantity)} by ${formatDate(order.dueDate)}`).join('; ').slice(0, 300),
        })),
      }),
    onSuccess: (indent) => {
      toast.success(`Purchase indent ${indent.indentNumber} drafted with ${picked.length} item(s)`, { duration: 6000 });
      rerun();
    },
    onError: (raiseError) => toast.error(getApiErrorMessage(raiseError, 'The indent could not be raised'), { duration: 7000 }),
  });

  const stats = [
    { label: 'Items to buy', value: formatNumber(data?.summary.itemsToBuy ?? 0), hint: 'Bought items with a suggested purchase' },
    { label: 'Items to make', value: formatNumber(data?.summary.itemsToMake ?? 0), hint: 'Made items with a suggested work order' },
    { label: 'Late already', value: formatNumber(data?.summary.lateOrders ?? 0), hint: 'Suggestions that cannot be there in time', alert: (data?.summary.lateOrders ?? 0) > 0 },
    { label: 'To act on this week', value: formatNumber(data?.summary.releaseThisWeek ?? 0), hint: `${formatNumber(data?.summary.toExpedite ?? 0)} order(s) to bring forward` },
  ];
  const buyable = rows.filter((row) => row.kind === 'buy' && row.plannedOrders.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Material Planning (MRP)</h1>
          <p className="mt-1 text-sm text-gray-500">
            What to buy and what to make, and by when: from confirmed sales orders, customer schedules, open work orders and minimum stock, against stock and what is already on order.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/indents" className={secondaryButtonClass}>Purchase Indents</Link>
          <button type="button" onClick={() => { setChosen({}); refetch(); }} disabled={isFetching} className={primaryButtonClass}>
            <ArrowPathIcon className={cn('-ml-0.5 mr-2 h-4 w-4', isFetching && 'animate-spin')} />
            Run Again
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-red-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      {(data?.warnings ?? []).length > 0 && (
        <ul className="space-y-1 rounded-lg bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-amber-600/20">
          {data?.warnings.map((warning) => (<li key={warning}>{warning}</li>))}
        </ul>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="space-y-3 border-b border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-gray-700">
            <label className="flex items-center gap-2">
              Plan for the next
              <select value={horizonDays} onChange={(event) => { setHorizonDays(Number(event.target.value)); setPage(1); }} aria-label="Horizon" className={cn(inputClass, 'w-32 py-1.5')}>
                {HORIZONS.map((days) => (<option key={days} value={days}>{days} days</option>))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={keepMinimumStock} onChange={(event) => { setKeepMinimumStock(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              Keep minimum stock
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={includeUnconfirmed} onChange={(event) => { setIncludeUnconfirmed(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              Include orders not yet confirmed
            </label>
            <label className="flex items-center gap-2" title="Forecasts from Demand Forecasting, for the part of each period not already on order or dispatched">
              <input type="checkbox" checked={useForecast} onChange={(event) => { setUseForecast(event.target.checked); setPage(1); }} aria-label="Plan for forecasts" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              Plan for forecasts
            </label>
            {data && <span className="text-xs text-gray-500">Up to {formatDate(data.horizonEnd)} · {formatNumber(data.summary.itemsPlanned)} item(s) planned{useForecast ? ` · ${formatNumber(data.summary.forecastLines ?? 0)} forecast line(s) not yet on order` : ''}</span>}
          </div>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by product or SKU" aria-label="Search items" className={cn(inputClass, 'pl-9')} />
            </div>
            <select value={show} onChange={(event) => { setShow(event.target.value as typeof show); setPage(1); }} aria-label="Show" className={cn(inputClass, 'lg:w-56')}>
              <option value="actions">What needs doing</option>
              <option value="all">Everything planned</option>
            </select>
            <select value={kind} onChange={(event) => { setKind(event.target.value as typeof kind); setPage(1); }} aria-label="Kind" className={cn(inputClass, 'lg:w-40')}>
              <option value="">Make and buy</option>
              <option value="buy">Buy</option>
              <option value="make">Make</option>
            </select>
            {canIndent && (
              <button type="button" onClick={() => raise.mutate()} disabled={picked.length === 0 || raise.isPending} className={primaryButtonClass}>
                {raise.isPending ? 'Raising…' : picked.length > 0 ? `Raise Indent for ${picked.length} Item(s)` : 'Raise Indent'}
              </button>
            )}
          </div>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {canIndent && (
                  <th scope="col" className={thClass}>
                    {buyable.length > 0 && (
                      <input
                        type="checkbox"
                        checked={buyable.every((row) => chosen[row.productId])}
                        onChange={(event) => setChosen((prev) => {
                          const next = { ...prev };
                          buyable.forEach((row) => { if (event.target.checked) next[row.productId] = row; else delete next[row.productId]; });
                          return next;
                        })}
                        aria-label="Tick every item to buy on this page"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    )}
                  </th>
                )}
                <th scope="col" className={thClass}>Item</th>
                <th scope="col" className={cn(thClass, 'text-right')}>In stock</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Wanted</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Coming</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Suggested</th>
                <th scope="col" className={thClass}>Order / start by</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The plan could not be worked out.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">Planning…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{search || kind ? 'No item matches' : show === 'actions' ? 'Nothing needs buying or making' : 'Nothing is planned'}</p>
                    <p className="mt-1 text-sm text-gray-500">Stock and what is on order cover the confirmed orders, schedules and work orders of the next {horizonDays} days.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.productId} className={cn('hover:bg-gray-50', chosen[row.productId] && 'bg-indigo-50/60')}>
                    {canIndent && (
                      <td className="px-4 py-3">
                        {row.kind === 'buy' && row.plannedOrders.length > 0 && (
                          <input
                            type="checkbox"
                            checked={!!chosen[row.productId]}
                            onChange={() => setChosen((prev) => {
                              const { [row.productId]: was, ...rest } = prev;
                              return was ? rest : { ...prev, [row.productId]: row };
                            })}
                            aria-label={`Put ${row.name} on the indent`}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.productId)} className="text-left text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.name}</button>
                      <p className="mt-0.5 text-xs text-gray-500">
                        <span className={cn(badgeClass, 'mr-2', KIND[row.kind].className)}>{KIND[row.kind].label}</span>
                        {row.sku}{row.leadTimeDays > 0 ? ` · ${row.leadTimeDays} day(s) lead time` : ' · no lead time set'}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatNumber(row.onHand)} <span className="text-gray-500">{row.unit}</span>
                      {row.minimumStock > 0 && <span className="block text-xs text-gray-500">minimum {formatNumber(row.minimumStock)}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.demand)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.scheduledReceipts)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">
                      {row.plannedOrders.length > 0 ? formatNumber(row.suggestedQuantity) : '—'}
                      {row.plannedOrders.length > 1 && <span className="block text-xs font-normal text-gray-500">in {row.plannedOrders.length} lots</span>}
                    </td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-sm', row.late ? 'font-medium text-red-600' : 'text-gray-700')}>
                      {row.firstReleaseDate ? (row.firstReleaseDate === data?.today ? 'Today' : formatDate(row.firstReleaseDate)) : '—'}
                      {row.late && <span className="block text-xs">Late already</span>}
                      {row.messages.length > 0 && <span className="block text-xs font-normal text-amber-700">{row.messages.length} note(s)</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.productId)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.name}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={!!viewing} onClose={() => setViewingId(null)} title={viewing?.name ?? 'Item'} size="xl">
        {viewing && data && <ItemPanel key={`${viewing.productId}-${viewing.ownLeadTimeDays}`} item={viewing} today={data.today} canMake={canMake} canEdit={canEdit} onChanged={rerun} onClose={() => setViewingId(null)} />}
      </Modal>
    </div>
  );
};

export default Mrp;
